import { useState } from 'react';
import { Copy, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useGetMyRedemptionsQuery } from '@/redux/points/points.api.slice';
import {
  RedemptionRecord,
  RedemptionStatus,
} from '@/redux/points/points.types';
import Pager from './Pager';
import { LedgerEmpty, LedgerSkeleton } from './LedgerStates';

const LIMIT = 10;

const STATUS: Record<RedemptionStatus, { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'border-green-600 text-green-700' },
  used: { label: 'Used', tone: 'border-neutral-400 text-neutral-600' },
  expired: { label: 'Expired', tone: 'border-red-500 text-red-700' },
};

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-7 w-7"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : `Copy coupon code ${code}`}
    >
      {copied ? (
        <CheckCircle className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

function CouponStub({ record }: { record: RedemptionRecord }) {
  const status = STATUS[record.status];
  const live = record.status === 'active';
  return (
    <li
      className={cn(
        'flex items-stretch overflow-hidden rounded-lg border',
        live ? 'bg-card' : 'bg-neutral-50'
      )}
    >
      <div className="flex w-20 shrink-0 flex-col items-center justify-center border-r border-dashed bg-green-50 px-2 py-3">
        <span className="font-display text-xl font-semibold tabular-nums text-green-800">
          ${record.pointsRedeemed}
        </span>
        <span className="text-[11px] text-green-900/70">
          {record.pointsRedeemed} pts
        </span>
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span
              className={cn(
                'truncate font-mono text-sm font-semibold tracking-wider',
                !live && 'text-muted-foreground line-through decoration-1'
              )}
            >
              {record.couponCode}
            </span>
            {live && <CopyButton code={record.couponCode} />}
          </div>
          <p className="text-xs tabular-nums text-muted-foreground">
            {format(new Date(record.createdAt), 'MMM d, yyyy')} · expires{' '}
            {format(new Date(record.expiresAt), 'MMM d, yyyy')}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 -rotate-6 rounded border-2 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider',
            status.tone
          )}
        >
          {status.label}
        </span>
      </div>
    </li>
  );
}

export default function RedemptionPanel() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetMyRedemptionsQuery({ page, limit: LIMIT });

  if (isLoading) return <LedgerSkeleton />;
  if (!data || data.records.length === 0) {
    return (
      <LedgerEmpty>
        No redemptions yet. Redeem your points for a discount coupon!
      </LedgerEmpty>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="flex flex-col gap-2">
        {data.records.map((record) => (
          <CouponStub key={record._id} record={record} />
        ))}
      </ul>
      <Pager page={page} total={data.total} limit={LIMIT} onPage={setPage} />
    </div>
  );
}
